/*
 * @Description: 客户端 Currency 判断逻辑
 *
 * 优先级：
 * 1. URL 参数 ?currency=XXX
 * 2. Cookie zl-currency
 * 3. IP 检测（API 返回的 is_default=1）
 * 4. 环境变量默认值
 */
'use client';

import { Currency } from '@/api/types/public';
import Cookie from 'js-cookie';
import { DEFAULT_CURRENCY } from '@/domain/currency/utils/defaultCurrency';
import { env } from '@/utils/env';
import { fetchCurrency } from '@/api/public';

interface CommonCurrencyInfo {
  source: 'URL' | 'Cookie' | 'IP' | 'Env' | 'Def';
  currency: Currency;
}

/**
 * 从 Cookie 中获取 currency
 */
function getCurrencyFromCookie(): string {
  // 尝试获取 currency name（旧版本）
  const zlCurrency = Cookie.get('zl-currency');
  if (zlCurrency) {
    try {
      // 可能是 JSON 格式
      const parsed = JSON.parse(zlCurrency) as Currency | string;
      if (parsed && typeof parsed === 'object' && parsed.name) {
        return parsed.name;
      }
      return typeof parsed === 'string' ? parsed : zlCurrency;
    } catch {
      // 可能是纯字符串
      return zlCurrency;
    }
  }
  return '';
}

/**
 * 根据 IP 检测
 * 如果服务端缓存了 currencyList，则客户端需要强制发起一次。若此次请求失败了，也不会 Fallback 用服务端的数据。
 */
export async function getDefaultCurrencyFromList(
  currencyList: Currency[],
  isForceFetch: boolean,
): Promise<Currency | null> {
  if (isForceFetch) {
    try {
      const json = await fetchCurrency();
      if (json?.code === 200 && Array.isArray(json?.data)) {
        currencyList = json.data;
      } else {
        currencyList = [];
      }
    } catch {
      currencyList = [];
    }
  }
  const defaultCurrency = currencyList.find((item) => +item.is_default === 1);
  return defaultCurrency || null;
}

/**
 * 获取环境变量默认货币。若没有则优先取第一个
 */
export function getEnvDefaultCurrency(currencyList: Currency[]): Currency | null {
  const defaultCurrencyName = env.NEXT_PUBLIC_DEFAULT_CURRENCY;
  const currency = currencyList.find((item) => item.name === defaultCurrencyName);

  if (currency) {
    return currency;
  }

  const firstCurrency = currencyList[0];
  if (firstCurrency) {
    return firstCurrency;
  }

  return null;
}

/**
 * 客户端确定 Currency
 */
async function determineClientCurrency(currencyList: Currency[]): Promise<CommonCurrencyInfo> {
  try {
    // 1. 检查 URL 参数（最高优先级）
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;
    const currencyFromURL = searchParams.get('currency') || searchParams.get('app_currency_code');
    if (currencyFromURL) {
      const targetCurrency = currencyFromURL.toUpperCase();
      const currency = currencyList.find((item) => item.name === targetCurrency);
      if (currency) {
        return { currency, source: 'URL' };
      }
    }

    // 2. 检查 Cookie（第二优先级）
    const currencyFromCookie = getCurrencyFromCookie();
    if (currencyFromCookie) {
      const currency = currencyList.find((item) => item.name === currencyFromCookie);
      if (currency) {
        return { currency, source: 'Cookie' };
      }
    }

    // 3. 检查 IP 检测（第三优先级）
    const defaultCurrency = await getDefaultCurrencyFromList(currencyList, true);
    if (defaultCurrency) {
      return { currency: defaultCurrency, source: 'IP' };
    }

    // 4. 使用环境变量默认值（兜底）
    const envCurrency = getEnvDefaultCurrency(currencyList);
    if (envCurrency) {
      return { currency: envCurrency, source: 'Env' };
    }
  } catch {
    // 静默走最后的兜底
  }

  return {
    source: 'Def',
    currency: DEFAULT_CURRENCY,
  };
}

/**
 * 客户端确定稳定的 Currency
 * @no-error
 */
export async function resolveClientCurrency(currencyList: Currency[]): Promise<CommonCurrencyInfo> {
  return await determineClientCurrency(currencyList);
}
